import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { adminService } from '@/api/services/admin';
import type { User } from '@/api/services/admin';
import { Card, CardHeader, CardTitle, CardContent } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { LoadingSpinner } from '@/components/ui/LoadingSpinner';

// ── Helpers ───────────────────────────────────────────────────────────────────

function fmt(n: number) {
  return n.toLocaleString();
}

function pct(n: number, total: number) {
  if (total === 0) return '0%';
  return Math.round((n / total) * 100) + '%';
}

// ── Simple horizontal bar (no chart lib) ─────────────────────────────────────

function HBar({ label, count, total, colorClass = 'bg-primary-500' }: {
  label: string;
  count: number;
  total: number;
  colorClass?: string;
}) {
  const widthPct = total === 0 ? 0 : Math.max(2, Math.round((count / total) * 100));
  return (
    <div className="flex items-center gap-3">
      <span className="text-xs text-text-secondary w-28 flex-shrink-0 truncate" title={label}>{label}</span>
      <div className="flex-1 h-4 bg-surface rounded-sm overflow-hidden">
        <div
          className={`h-full ${colorClass} rounded-sm transition-all duration-300`}
          style={{ width: `${widthPct}%` }}
        />
      </div>
      <span className="text-xs text-text-primary tabular-nums w-20 text-right flex-shrink-0">
        {fmt(count)} <span className="text-text-muted">({pct(count, total)})</span>
      </span>
    </div>
  );
}

// ── Stub card ─────────────────────────────────────────────────────────────────

function StubCard({ title, note }: { title: string; note: string }) {
  return (
    <Card className="border-dashed">
      <CardHeader>
        <CardTitle className="text-base">{title}</CardTitle>
      </CardHeader>
      <CardContent>
        <p className="text-sm text-text-muted italic">{note}</p>
      </CardContent>
    </Card>
  );
}

// ── Dashboard ─────────────────────────────────────────────────────────────────

export default function Dashboard() {
  const [users, setUsers] = useState<User[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const fetchUsers = () => {
    setLoading(true);
    setError('');
    adminService
      .getUsers()
      .then((data) => setUsers(data))
      .catch((e) => setError(e instanceof Error ? e.message : 'Failed to load users.'))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    fetchUsers();
  }, []);

  // ── Derived aggregates ─────────────────────────────────────────────────────

  const total = users.length;
  const activeCount = users.filter((u) => u.active).length;
  const disabledCount = total - activeCount;
  const subscribedCount = users.filter((u) => u.status.subscription.is_subscribed).length;
  const totalCredits = users.reduce((sum, u) => sum + u.status.subscription.available_credits, 0);

  // Onboarding funnel
  const withFaAssessment = users.filter((u) => u.status.fa_assessment > 0).length;
  const withFaAnalysis = users.filter((u) => u.status.fa_analysis > 0).length;

  // Credit-balance distribution
  const creditBuckets = [
    { label: '0 credits', count: users.filter((u) => u.status.subscription.available_credits === 0).length },
    { label: '1–10', count: users.filter((u) => { const c = u.status.subscription.available_credits; return c >= 1 && c <= 10; }).length },
    { label: '11–50', count: users.filter((u) => { const c = u.status.subscription.available_credits; return c >= 11 && c <= 50; }).length },
    { label: '51–100', count: users.filter((u) => { const c = u.status.subscription.available_credits; return c >= 51 && c <= 100; }).length },
    { label: '100+', count: users.filter((u) => u.status.subscription.available_credits > 100).length },
  ];

  // Top 5 by prospect_search
  const topBySearch = [...users]
    .sort((a, b) => b.status.prospect_search - a.status.prospect_search)
    .slice(0, 5);

  // Top 5 by prospect_messages
  const topByMessages = [...users]
    .sort((a, b) => b.status.prospect_messages - a.status.prospect_messages)
    .slice(0, 5);

  // Top orgs
  const orgMap: Record<string, number> = {};
  for (const u of users) {
    const org = u.org_name || '(no org)';
    orgMap[org] = (orgMap[org] ?? 0) + 1;
  }
  const topOrgs = Object.entries(orgMap)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 10);

  // Prompt slot meta (copied from Prompts.tsx slotMeta to avoid circular import)
  const promptSlots = [
    { name: 'message_prompt', description: 'LLM prompts for prospect message generation (email, linkedin-connection, linkedin-inmail).' },
    { name: 'campaign_message_template', description: 'Static 401k campaign messages — literal substitution, no LLM.' },
    { name: 'fa_analysis_prompt', description: 'LLM prompt for the FA analysis pass (per-archetype LinkedIn profile rewrite).' },
    { name: 'prospect_research_prompt', description: 'LLM prompt for prospect personality research.' },
  ];

  // ── Loading / error ────────────────────────────────────────────────────────

  if (loading) {
    return (
      <div className="flex items-center justify-center py-24">
        <LoadingSpinner />
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-4">
        <h1 className="text-2xl font-semibold text-text-primary">Dashboard</h1>
        <div className="flex items-center gap-3 p-4 rounded-md border border-error-200 dark:border-error-800/30 bg-error-50 dark:bg-error-900/20">
          <p className="text-sm text-error-700 dark:text-error-400 flex-1">{error}</p>
          <Button variant="secondary" size="sm" onClick={fetchUsers}>Retry</Button>
        </div>
      </div>
    );
  }

  // ── Render ─────────────────────────────────────────────────────────────────

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
        <h1 className="text-2xl font-semibold text-text-primary">Dashboard</h1>
        <p className="text-sm text-text-secondary mt-1">
          Snapshot of the Rainmaker platform — derived from <code className="font-mono text-xs bg-surface px-1 py-0.5 rounded">/adm/get_users</code>. More data coming as backend endpoints ship.
        </p>
      </div>

      {/* Tiles row */}
      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent>
            <p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Total users</p>
            <p className="text-3xl font-bold text-text-primary tabular-nums">{fmt(total)}</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Active users</p>
            <p className="text-3xl font-bold text-text-primary tabular-nums">{fmt(activeCount)}</p>
            <p className="text-xs text-text-muted mt-1">{fmt(disabledCount)} disabled</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Subscribed</p>
            <p className="text-3xl font-bold text-text-primary tabular-nums">{fmt(subscribedCount)}</p>
            <p className="text-xs text-text-muted mt-1">{pct(subscribedCount, total)} of total</p>
          </CardContent>
        </Card>

        <Card>
          <CardContent>
            <p className="text-xs text-text-secondary uppercase tracking-wider mb-1">Platform credits</p>
            <p className="text-3xl font-bold text-text-primary tabular-nums">{fmt(totalCredits)}</p>
            <p className="text-xs text-text-muted mt-1">across all users</p>
          </CardContent>
        </Card>
      </div>

      {/* Two-column row: Onboarding funnel + Credit distribution */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        {/* Onboarding funnel */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Onboarding funnel</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            <HBar label="Any FA assessment" count={withFaAssessment} total={total} colorClass="bg-primary-500" />
            <HBar label="Any FA analysis" count={withFaAnalysis} total={total} colorClass="bg-primary-400" />
          </CardContent>
        </Card>

        {/* Credit-balance distribution */}
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Credit-balance distribution</CardTitle>
          </CardHeader>
          <CardContent className="space-y-3">
            {creditBuckets.map((b) => (
              <HBar key={b.label} label={b.label} count={b.count} total={total} colorClass="bg-success-500" />
            ))}
          </CardContent>
        </Card>
      </div>

      {/* Two-column row: Top by search + Top by messages */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top 5 — prospect searches</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">Name</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">Email</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-text-muted uppercase">Searches</th>
                </tr>
              </thead>
              <tbody>
                {topBySearch.map((u) => (
                  <tr key={u.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2 text-sm text-text-primary">{u.full_name || <span className="italic text-text-muted">—</span>}</td>
                    <td className="px-4 py-2 text-xs text-text-secondary truncate max-w-[140px]">{u.email}</td>
                    <td className="px-4 py-2 text-sm text-text-primary tabular-nums text-right">{fmt(u.status.prospect_search)}</td>
                  </tr>
                ))}
                {topBySearch.length === 0 && (
                  <tr><td colSpan={3} className="px-4 py-4 text-center text-sm text-text-muted">No data</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle className="text-base">Top 5 — prospect messages</CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b border-border">
                  <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">Name</th>
                  <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">Email</th>
                  <th className="text-right px-4 py-2 text-xs font-medium text-text-muted uppercase">Messages</th>
                </tr>
              </thead>
              <tbody>
                {topByMessages.map((u) => (
                  <tr key={u.id} className="border-b border-border last:border-0">
                    <td className="px-4 py-2 text-sm text-text-primary">{u.full_name || <span className="italic text-text-muted">—</span>}</td>
                    <td className="px-4 py-2 text-xs text-text-secondary truncate max-w-[140px]">{u.email}</td>
                    <td className="px-4 py-2 text-sm text-text-primary tabular-nums text-right">{fmt(u.status.prospect_messages)}</td>
                  </tr>
                ))}
                {topByMessages.length === 0 && (
                  <tr><td colSpan={3} className="px-4 py-4 text-center text-sm text-text-muted">No data</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>
      </div>

      {/* Top orgs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Top organizations</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">Organization</th>
                <th className="text-right px-4 py-2 text-xs font-medium text-text-muted uppercase">Users</th>
              </tr>
            </thead>
            <tbody>
              {topOrgs.map(([org, count]) => (
                <tr key={org} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 text-sm text-text-primary">{org}</td>
                  <td className="px-4 py-2 text-sm text-text-primary tabular-nums text-right">{fmt(count)}</td>
                </tr>
              ))}
              {topOrgs.length === 0 && (
                <tr><td colSpan={2} className="px-4 py-4 text-center text-sm text-text-muted">No data</td></tr>
              )}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Live prompts */}
      <Card>
        <CardHeader>
          <CardTitle className="text-base">Live prompts</CardTitle>
        </CardHeader>
        <CardContent className="p-0">
          <table className="w-full text-sm">
            <thead>
              <tr className="border-b border-border">
                <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">Slot</th>
                <th className="text-left px-4 py-2 text-xs font-medium text-text-muted uppercase">Description</th>
              </tr>
            </thead>
            <tbody>
              {promptSlots.map((slot) => (
                <tr key={slot.name} className="border-b border-border last:border-0">
                  <td className="px-4 py-2 font-mono text-xs text-text-primary whitespace-nowrap">
                    <Link to="/prompts" className="text-primary-500 dark:text-primary-400 hover:underline">
                      {slot.name}
                    </Link>
                  </td>
                  <td className="px-4 py-2 text-sm text-text-secondary">{slot.description}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {/* Stub cards: recent admin actions + recent errors */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <StubCard
          title="Recent admin actions"
          note="Audit endpoint pending — once backend ships GET /adm/audit, recent actions will appear here."
        />
        <StubCard
          title="Recent errors"
          note="Error stream endpoint pending — once backend ships GET /adm/errors, recent errors will appear here."
        />
      </div>
    </div>
  );
}
